/**
 * Unified API Configuration - Single Source of Truth
 *
 * This module consolidates all API URL references to prevent fragmentation
 * and ensure consistent backend targeting across all environments.
 *
 * HARDCODED PORT 8000 for local development to prevent port mismatches.
 *
 * Supports legacy v1 (removed) and v2 (FastAPI) endpoints:
 * - v1: /api/* (legacy, removed)
 * - v2: /api/v2/* (FastAPI)
 *
 * @canonical NEXT_PUBLIC_BACKEND_API_URL - The only env var that should be used
 */

// Default fallbacks for local development - HARDCODED PORT 8000
const DEFAULT_LOCAL_API = "http://localhost:8000/api";
const DEFAULT_LOCAL_API_V2 = "http://localhost:8000/api/v2";
const DEFAULT_LOCAL_WS = "ws://localhost:8000";
const DEFAULT_LOCAL_WS_V2 = "ws://localhost:8000/ws/v2";

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);

function isLoopbackHost(hostname: string | null | undefined): boolean {
  return typeof hostname === "string" && LOOPBACK_HOSTS.has(hostname.toLowerCase());
}

function normalizeLoopbackUrl(url: string): string {
  const trimmed = url.replace(/\/+$/, "");

  if (typeof window === "undefined") {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    const currentHostname = window.location.hostname;

    if (isLoopbackHost(parsed.hostname) && isLoopbackHost(currentHostname)) {
      parsed.hostname = currentHostname;
      return parsed.toString().replace(/\/+$/, "");
    }
  } catch {
    return trimmed;
  }

  return trimmed;
}

export function getWebSocketAuthProtocols(accessToken: string): string[] {
  return ["bearer", accessToken];
}

// =============================================================================
// SIG-212 — BFF proxy (client-side requests go same-origin)
// =============================================================================

/**
 * Same-origin BFF proxy mount (app/api/proxy/[...path]/route.ts). Client-side
 * code calls the proxy — which attaches the backend token server-side from
 * the httpOnly session cookie — instead of the backend origin directly.
 * Client-only: server-side code keeps calling the backend origin directly.
 */
export const PROXY_BASE_PATH = "/api/proxy";

/**
 * Test-env carve-out: legacy component tests issue REAL fetches (jsdom has
 * no global fetch mock) that historically hit the backend origin directly
 * and were written against that behavior. Routing them through a live Next
 * dev server on :3000 serializes them behind its single compile thread and
 * times out marginal tests. Under vitest the proxied getters return the
 * direct backend URL — the proxy contract itself is covered explicitly by
 * tests/sig-212-slice2-bff-proxy.test.ts and the api-client buildUrl tests
 * (which mock these getters with proxied values).
 */
const IS_TEST_ENV = typeof process !== "undefined" && !!process.env.VITEST;

/** Client-side replacement for getApiBaseUrl() — proxied, same-origin. */
export function getProxiedApiBaseUrl(): string {
  if (typeof window === "undefined") {
    throw new Error("getProxiedApiBaseUrl is client-side only — use getApiBaseUrl() on the server");
  }
  if (IS_TEST_ENV) return getApiBaseUrl();
  return `${window.location.origin}${PROXY_BASE_PATH}/api`;
}

/** Client-side replacement for getApiV2BaseUrl() — proxied, same-origin. */
export function getProxiedApiV2BaseUrl(): string {
  if (typeof window === "undefined") {
    throw new Error("getProxiedApiV2BaseUrl is client-side only — use getApiV2BaseUrl() on the server");
  }
  if (IS_TEST_ENV) return getApiV2BaseUrl();
  return `${window.location.origin}${PROXY_BASE_PATH}/api/v2`;
}


/**
 * Get the base API URL with /api suffix.
 * This is the canonical URL getter for REST API calls.
 *
 * @returns The API base URL (e.g., "http://localhost:8000/api")
 */
export function getApiBaseUrl(): string {
  const url =
    process.env.NEXT_PUBLIC_BACKEND_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    DEFAULT_LOCAL_API;
  return normalizeLoopbackUrl(url);
}

/**
 * Get the REST API base URL without the /api suffix.
 * Use this for hooks that append their own /api prefix.
 *
 * @returns The API base URL without /api suffix (e.g., "http://localhost:8000")
 */
export function getRestApiBase(): string {
  const base = getApiBaseUrl();
  // Strip /api suffix for hooks that add it themselves
  return base.endsWith("/api") ? base.slice(0, -4) : base;
}

/**
 * Get the WebSocket URL derived from the API base URL.
 * Converts http(s) to ws(s) and removes /api suffix.
 *
 * HARDCODED to use port 8000 for local development to prevent port mismatches.
 *
 * @param path - Optional path to append (e.g., "/ws/v2/unified")
 * @returns The WebSocket URL (e.g., "ws://localhost:8000/ws/v2/unified")
 */
export function getWebSocketUrl(path: string = ""): string {
  const apiUrl = getApiBaseUrl();

  // Check for localhost - use hardcoded default
  const isLocalApi =
    apiUrl.includes("localhost") ||
    apiUrl.includes("127.0.0.1") ||
    apiUrl.includes("0.0.0.0");
  if (isLocalApi) {
    const wsUrl = normalizeLoopbackUrl(DEFAULT_LOCAL_WS);
    return path ? `${wsUrl}${path}` : wsUrl;
  }

  // Check for explicit environment variable (non-local only)
  const envWsUrl = process.env.NEXT_PUBLIC_WEBSOCKET_URL;
  if (envWsUrl) {
    const wsUrl = normalizeLoopbackUrl(envWsUrl);
    return path ? `${wsUrl}${path}` : wsUrl;
  }

  // Derive from API URL as fallback
  const wsUrl = apiUrl.replace(/^http/, "ws").replace(/\/api\/?$/, "");
  const normalizedWsUrl = normalizeLoopbackUrl(wsUrl);
  return path ? `${normalizedWsUrl}${path}` : normalizedWsUrl;
}

// =============================================================================
// V2 API Configuration (FastAPI)
// =============================================================================

/**
 * Get the v2 API base URL (FastAPI endpoints).
 * Supports explicit NEXT_PUBLIC_BACKEND_API_V2_URL or derives from v1 URL.
 *
 * @returns The v2 API base URL (e.g., "http://localhost:8000/api/v2")
 */
export function getApiV2BaseUrl(): string {
  // Check for explicit v2 URL first
  const v2Url = process.env.NEXT_PUBLIC_BACKEND_API_V2_URL;
  if (v2Url) {
    return normalizeLoopbackUrl(v2Url); // Strip trailing slash + normalize loopback host
  }

  // Check for localhost - use hardcoded default
  const v1Url = getApiBaseUrl();
  const isLocalApi =
    v1Url.includes("localhost") ||
    v1Url.includes("127.0.0.1") ||
    v1Url.includes("0.0.0.0");
  if (isLocalApi) {
    return normalizeLoopbackUrl(DEFAULT_LOCAL_API_V2);
  }

  // Derive from v1 URL: append /v2
  if (v1Url.endsWith("/api")) {
    return `${v1Url}/v2`;
  }

  // Fallback: append /api/v2
  return `${v1Url}/api/v2`;
}

/**
 * Check if API v2 is globally enabled via environment variable.
 * This is a global kill switch; per-endpoint flags are in feature-flags.ts.
 *
 * @returns True if NEXT_PUBLIC_API_V2_ENABLED is 'true'
 */
export function isApiV2Enabled(): boolean {
  return process.env.NEXT_PUBLIC_API_V2_ENABLED === "true";
}

/**
 * Get the v2 WebSocket URL (FastAPI native WebSocket).
 * Connects to /ws/v2/* endpoints.
 *
 * @param path - Optional path to append (e.g., "/unified")
 * @returns The v2 WebSocket URL (e.g., "ws://localhost:8000/ws/v2/unified")
 */
export function getWebSocketV2Url(path: string = ""): string {
  const apiV2Url = getApiV2BaseUrl();

  // Check for localhost - use hardcoded default
  const isLocalApi =
    apiV2Url.includes("localhost") ||
    apiV2Url.includes("127.0.0.1") ||
    apiV2Url.includes("0.0.0.0");
  if (isLocalApi) {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${normalizeLoopbackUrl(DEFAULT_LOCAL_WS_V2)}${cleanPath}`;
  }

  // Check for explicit environment variable
  const envWsV2Url = process.env.NEXT_PUBLIC_WEBSOCKET_V2_URL;
  if (envWsV2Url) {
    const wsUrl = normalizeLoopbackUrl(envWsV2Url);
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${wsUrl}${cleanPath}`;
  }

  // Derive from API v2 URL: convert http(s) to ws(s) and adjust path
  const wsUrl = apiV2Url.replace(/^http/, "ws").replace(/\/api\/v2\/?$/, "/ws/v2");
  const normalizedWsUrl = normalizeLoopbackUrl(wsUrl);
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedWsUrl}${cleanPath}`;
}

/**
 * Check if WebSocket v2 is enabled via environment variable.
 *
 * @returns True if NEXT_PUBLIC_WS_V2_ENABLED is 'true'
 */
export function isWsV2Enabled(): boolean {
  return process.env.NEXT_PUBLIC_WS_V2_ENABLED !== "false";
}

/**
 * Get the unified v2 WebSocket URL.
 * Convenience wrapper for the main v2 WebSocket endpoint.
 *
 * @returns The unified v2 WebSocket URL (e.g., "ws://localhost:8000/ws/v2/unified")
 */
export function getUnifiedWebSocketV2Url(): string {
  return getWebSocketV2Url("/unified");
}
